<div class="revision-review" [class.variant-client]="variant === 'client'" [class.variant-business]="variant === 'business'" [class.is-record]="isRecord">
  @if (!isRecord) {
  <section class="review-section">
    @if (view.overview.collapseToSingleLine) {
      <div class="overview-row">
        <span class="overview-label">Contract value</span>
        <span class="overview-value">{{ taggedMoney(view.overview.afterContractValue) }}</span>
      </div>
    } @else {
      @if (view.overview.hasAcceptedAdjustments) {
        <div class="overview-block">
          <div class="overview-heading">Before this revision</div>
          <div class="overview-row">
            <span class="overview-label">Original contract</span>
            <span class="overview-value">{{ money(view.overview.originalContractValue) }}</span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Accepted variations</span>
            <span class="overview-value" [class]="netChangeClass(view.overview.acceptedVariationsNet)">
              {{ signedMoney(view.overview.acceptedVariationsNet) }}
            </span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Current contract value</span>
            <span class="overview-value">{{ taggedMoney(view.overview.beforeContractValue) }}</span>
          </div>
        </div>
      } @else {
        <div class="overview-row">
          <span class="overview-label">Contract value</span>
          <span class="overview-value">{{ taggedMoney(view.overview.beforeContractValue) }}</span>
        </div>
      }
      <div class="overview-block">
        <div class="overview-heading">Changes in this revision</div>
        @if (view.overview.noValueChange) {
          <div class="overview-row muted">No change to contract value</div>
        } @else {
          @for (line of view.overview.changeLines; track line.label) {
            <div class="overview-row">
              <span class="overview-label">{{ line.label }}</span>
              <span class="overview-value" [class]="netChangeClass(line.amount)">{{ signedMoney(line.amount) }}</span>
            </div>
          }
          <div class="overview-row">
            <span class="overview-label">Revised contract value</span>
            <span class="overview-value">{{ taggedMoney(view.overview.afterContractValue) }}</span>
          </div>
          <div class="overview-row">
            <span class="overview-label">Net change</span>
            <span class="overview-value" [class]="netChangeClass(view.overview.netChange)">
              {{ signedMoney(view.overview.netChange) }}
            </span>
          </div>
        }
      </div>
    }
  </section>
  }

  @if (!isRecord || datesChanged) {
  <section class="review-card">
    <div class="fields fields-dates">
      <div class="field">
        <span class="field-label">Start date</span>
        <span class="field-value">{{ dateLabel(view.projectDates.startDate) }}</span>
        @if (view.projectDates.previousStartDate) {
          <span class="was-previously">was previously {{ dateLabel(view.projectDates.previousStartDate) }}</span>
        }
        @if (pushedNote(view.projectDates.startPushedDays); as note) {
          <span class="derived-note">{{ note }}</span>
        }
      </div>
      <div class="field">
        <span class="field-label">End date</span>
        <span class="field-value">{{ dateLabel(view.projectDates.endDate) }}</span>
        @if (view.projectDates.previousEndDate) {
          <span class="was-previously">was previously {{ dateLabel(view.projectDates.previousEndDate) }}</span>
        }
        @if (pushedNote(view.projectDates.endPushedDays); as note) {
          <span class="derived-note">{{ note }}</span>
        }
      </div>
    </div>
  </section>
  }

  @if (view.milestones.length) {
  <section class="review-section">
    <div class="section-heading">
      <h3>{{ isRecord ? "Changes to the project" : "Milestones" }}</h3>
    </div>
    <div class="card-stack">
      @for (milestone of view.milestones; track milestone.id) {
        @if (isDetailedMilestone(milestone)) {
        <article class="review-card" [class.card-new]="milestone.tag === 'new'" [class.card-deleted]="milestone.tag === 'deleted'">
          @if (milestone.tag) {
            <span class="state-tag" [attr.data-tag]="milestone.tag">{{ tagLabel(milestone.tag) }}</span>
          }
          <div class="card-name">{{ milestone.name }}</div>
          @if (milestone.previousName) {
            <div class="was-previously">was previously {{ milestone.previousName }}</div>
          }
          @if (milestone.description) {
            <p class="card-description">{{ milestone.description }}</p>
          }
          @if (milestone.previousDescription) {
            <div class="was-previously">was previously {{ milestone.previousDescription }}</div>
          }
          <div class="fields">
            <div class="field field-amount">
              <span class="field-label">Amount</span>
              <span class="field-value">{{ money(milestone.amount) }}</span>
            </div>
            <div class="field">
              <span class="field-label">Start date</span>
              <span class="field-value">{{ dateLabel(milestone.startDate) }}</span>
              @if (milestone.previousStartDate) {
                <span class="was-previously">was previously {{ dateLabel(milestone.previousStartDate) }}</span>
              }
              @if (pushedNote(milestone.startPushedDays); as note) {
                <span class="derived-note">{{ note }}</span>
              }
            </div>
            <div class="field">
              <span class="field-label">End date</span>
              <span class="field-value">{{ dateLabel(milestone.endDate) }}</span>
              @if (milestone.previousEndDate) {
                <span class="was-previously">was previously {{ dateLabel(milestone.previousEndDate) }}</span>
              }
              @if (pushedNote(milestone.endPushedDays); as note) {
                <span class="derived-note">{{ note }}</span>
              }
            </div>
          </div>
          @if (milestone.deletionSentence) {
            <p class="deletion-copy">{{ variant === "client" ? "This milestone will be deleted from the project. The work will not be carried out and you will not be charged for it." : milestone.deletionSentence }}</p>
            <div class="deletion-strip amount-decrease">Contract value reduces by {{ money(milestone.deletionStripAmount) }}</div>
          }
          @if (milestone.submilestones.length) {
            <div class="submilestone-block">
              @for (sub of milestone.submilestones; track sub.id; let index = $index) {
                <div class="submilestone-row" [class.is-deleted]="sub.tag === 'deleted'">
                  <div class="sub-main">
                    <span class="sub-name">
                      @if (sub.tag) {
                        <span class="state-tag inline" [attr.data-tag]="sub.tag">{{ tagLabel(sub.tag) }}</span>
                      }
                      {{ index + 1 }}. {{ sub.name }}
                    </span>
                    @if (sub.description) {
                      <span class="sub-desc">{{ sub.description }}</span>
                    }
                  </div>
                  <span class="sub-amount">{{ money(sub.amount) }}</span>
                  <span class="sub-dates">{{ dateLabel(sub.startDate) }} – {{ dateLabel(sub.endDate) }}</span>
                </div>
              }
            </div>
          }
        </article>
        } @else {
        <div class="quiet-row">
          <div class="quiet-main">
            <div class="quiet-name">{{ milestone.name }}</div>
            @if (milestone.description) {
              <div class="quiet-desc">{{ milestone.description }}</div>
            }
            <div class="quiet-meta">{{ dateLabel(milestone.startDate) }} – {{ dateLabel(milestone.endDate) }}</div>
            @if (milestone.submilestones.length) {
              <div class="quiet-subs">
                @for (sub of milestone.submilestones; track sub.id; let index = $index) {
                  <div class="quiet-sub">
                    <span>{{ index + 1 }}. {{ sub.name }}</span>
                    <span class="nowrap">{{ money(sub.amount) }}</span>
                  </div>
                }
              </div>
            }
          </div>
          <div class="quiet-amount">{{ money(milestone.amount) }}</div>
        </div>
        }
      }
    </div>
  </section>
  }

  @if (view.adjustments.length) {
    <section class="review-section">
      @if (additionAdjustments.length) {
        <div class="section-heading">
          <h3>Variation orders</h3>
        </div>
        <div class="card-stack">
          @for (adjustment of additionAdjustments; track adjustment.id) {
            @if (adjustment.tag) {
            <article class="review-card" [class.card-new]="adjustment.tag === 'new'">
              <span class="state-tag" [attr.data-tag]="adjustment.tag">{{ tagLabel(adjustment.tag) }}</span>
              <div class="card-name">{{ adjustment.label }}{{ adjustment.displayCode ? " " + adjustment.displayCode : "" }}</div>
              @if (adjustment.description) {
                <p class="card-description">{{ adjustment.description }}</p>
              }
              <div class="fields fields-dates">
                <div class="field">
                  <span class="field-label">Amount</span>
                  <span class="field-value" [class]="netChangeClass(adjustment.signedAmount)">{{ adjustment.amount ? money(adjustment.signedAmount) : "—" }}</span>
                </div>
                <div class="field">
                  <span class="field-label">Status</span>
                  <span class="field-value">{{ moneyStateLabel(adjustment.moneyLabel) }}</span>
                </div>
              </div>
            </article>
            } @else {
            <div class="quiet-row">
              <div class="quiet-main">
                <div class="quiet-name">{{ adjustment.label }}{{ adjustment.displayCode ? " " + adjustment.displayCode : "" }}</div>
                @if (adjustment.description) {
                  <div class="quiet-desc">{{ adjustment.description }}</div>
                }
                <div class="quiet-meta">{{ moneyStateLabel(adjustment.moneyLabel) }}</div>
              </div>
              <div class="quiet-amount">{{ adjustment.amount ? money(adjustment.signedAmount) : "—" }}</div>
            </div>
            }
          }
        </div>
      }
      @if (deductionAdjustments.length) {
        <div class="section-heading">
          <h3>Deduction orders</h3>
        </div>
        <div class="card-stack">
          @for (adjustment of deductionAdjustments; track adjustment.id) {
            @if (adjustment.tag) {
            <article class="review-card" [class.card-new]="adjustment.tag === 'new'">
              <span class="state-tag" [attr.data-tag]="adjustment.tag">{{ tagLabel(adjustment.tag) }}</span>
              <div class="card-name">{{ adjustment.label }}{{ adjustment.displayCode ? " " + adjustment.displayCode : "" }}</div>
              @if (adjustment.description) {
                <p class="card-description">{{ adjustment.description }}</p>
              }
              <div class="fields fields-dates">
                <div class="field">
                  <span class="field-label">Amount</span>
                  <span class="field-value" [class]="netChangeClass(adjustment.signedAmount)">{{ adjustment.amount ? money(adjustment.signedAmount) : "—" }}</span>
                </div>
                <div class="field">
                  <span class="field-label">Status</span>
                  <span class="field-value">{{ moneyStateLabel(adjustment.moneyLabel) }}</span>
                </div>
              </div>
            </article>
            } @else {
            <div class="quiet-row">
              <div class="quiet-main">
                <div class="quiet-name">{{ adjustment.label }}{{ adjustment.displayCode ? " " + adjustment.displayCode : "" }}</div>
                @if (adjustment.description) {
                  <div class="quiet-desc">{{ adjustment.description }}</div>
                }
                <div class="quiet-meta">{{ moneyStateLabel(adjustment.moneyLabel) }}</div>
              </div>
              <div class="quiet-amount">{{ adjustment.amount ? money(adjustment.signedAmount) : "—" }}</div>
            </div>
            }
          }
        </div>
      }
    </section>
  }

  @if (view.invoices.length) {
  <section class="review-section">
    <div class="section-heading">
      <h3>Payment schedule</h3>
    </div>
    <div class="schedule-scroll">
      <table class="schedule">
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Associated items</th>
            <th class="num">Amount</th>
            <th>Due date</th>
            @if (showClientFee) {
              <th class="num">Handshake fee</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (invoice of scheduledInvoices; track invoice.id) {
            <tr [class.row-new]="invoice.tag === 'new'" [class.row-deleted]="invoice.tag === 'deleted'">
              <td>
                <div class="schedule-title">{{ invoice.issuedLabel }}</div>
                @if (invoice.tag) {
                  <span class="state-tag inline" [attr.data-tag]="invoice.tag">{{ tagLabel(invoice.tag) }}</span>
                }
              </td>
              <td>
                @for (line of invoice.lines; track line.id) {
                  <div class="invoice-line">
                    <span>{{ line.name }}</span>
                    <span class="nowrap" [class]="line.kind === 'adjustment' ? netChangeClass(line.amount) : ''">{{ line.kind === "adjustment" ? signedMoney(line.amount) : money(line.amount) }}</span>
                  </div>
                }
              </td>
              <td class="num">{{ money(invoiceAmount(invoice)) }}</td>
              <td>
                <span class="nowrap">{{ dateLabel(invoice.dueDate) }}</span>
                @if (invoice.previousDueDate) {
                  <span class="was-previously">was previously {{ dateLabel(invoice.previousDueDate) }}</span>
                }
              </td>
              @if (showClientFee) {
                <td class="num">{{ money(clientFee(invoice)) }}</td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (!isRecord) {
      <div class="overview-row remaining">
        <span class="overview-label">Remaining to pay</span>
        <span class="overview-value">{{ taggedMoney(view.remainingToPay) }}</span>
      </div>
    }
  </section>
  }
</div>
